import { useQuery } from "@tanstack/react-query";
import { ChevronDown, LogOut, Settings, ShieldCheck, UserRound } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { api } from "@/core/api/client";
import { useT } from "@/core/i18n";
import { useSession } from "@/core/state/session";
import { AppLogo } from "@/shared/ui/AppLogo";
import { TopbarPopover } from "./TopbarPopover";

export function ProfileMenu() {
  const t = useT();
  const identity = useSession((state) => state.identity);
  const setIdentity = useSession((state) => state.setIdentity);
  const navigate = useNavigate();
  const location = useLocation();
  const name = identity?.displayName || identity?.username || t("shell.memberFallback");
  const detail = [identity?.username, identity?.tenantSlug].filter(Boolean).join(" · ");
  const profile = useQuery({
    queryKey: ["profile-avatar", identity?.tenantSlug, identity?.userId],
    enabled: Boolean(identity?.tenantSlug),
    queryFn: () => api.account(identity!.tenantSlug) as Promise<{ user?: { avatar_url?: string; avatar_fallback_url?: string } }>,
    staleTime: 60_000,
  });
  const avatar = profile.data?.user?.avatar_url;
  const avatarFallback = profile.data?.user?.avatar_fallback_url;
  const mark = avatar ? <AppLogo className="avatar avatar-preview" url={avatar} fallbackUrl={avatarFallback} /> : <span className="avatar" aria-hidden="true">{name.slice(0, 1).toUpperCase()}</span>;

  return (
    <TopbarPopover
      label={name}
      menuLabel={t("shell.profileMenu")}
      buttonClassName="profile-chip"
      panelClassName="profile-menu"
      panel={(close) => {
        function go(path: string) {
          close();
          if (location.pathname !== path) navigate(path);
        }
        async function logout() {
          close();
          await api.logout().catch(() => undefined);
          setIdentity(null);
          navigate("/login");
        }
        return (
          <>
            <div className="profile-menu-identity">
              {mark}
              <span>
                <strong>{name}</strong>
                {detail ? <small>{detail}</small> : null}
              </span>
            </div>
            <p className="eyebrow">{t("shell.quickActions")}</p>
            <div className="profile-shortcuts">
              <button type="button" role="menuitem" className="profile-shortcut" onClick={() => go("/account")}>
                <UserRound size={16} strokeWidth={1.8} aria-hidden="true" />
                <span>{t("common.nav.account")}</span>
              </button>
              <button type="button" role="menuitem" className="profile-shortcut" onClick={() => go("/security")}>
                <ShieldCheck size={16} strokeWidth={1.8} aria-hidden="true" />
                <span>{t("common.nav.security")}</span>
              </button>
            </div>
            <div className="profile-menu-list">
              <button type="button" role="menuitem" className="profile-menu-row" onClick={() => go("/settings")}>
                <Settings size={16} strokeWidth={1.8} aria-hidden="true" />
                <span>{t("common.nav.settings")}</span>
              </button>
              <button type="button" role="menuitem" className="profile-menu-row" onClick={() => void logout()}>
                <LogOut size={16} strokeWidth={1.8} aria-hidden="true" />
                <span>{t("common.buttons.logout")}</span>
              </button>
            </div>
          </>
        );
      }}
    >
      {mark}
      <span>{name}</span>
      <ChevronDown size={14} aria-hidden="true" />
    </TopbarPopover>
  );
}
